/** @jsx jsx */
/* eslint-disable max-len, no-nested-ternary, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from 'react';
import { css, jsx } from '@emotion/core';
import { Link } from 'react-router-dom';
import { dailyShiftPath } from 'domain/attend/shiftBoard/menuLinks';
import {
  BoardData, DayHeader, SimulationShift, StaffInfo,
} from 'domain/attend/shiftBoard/types';
import {
  defaultTimeFormatter, DOW_LABELS, parseDateStr, spanMinutes, TimeFormatter, workMinutesOf,
} from 'domain/attend/shiftBoard/time';
import { validateCell, validateTimes } from 'domain/attend/shiftBoard/validation';
import { PatternColor, FreeTimes } from './PatternPalette';
import { HopeLabel } from './BoardGrid';
import { mock } from './styles';
import BoardSelect from './BoardSelect';

const OVERLAY_PAD = 16;

const styles = {
  // iPad で高さが見切れていた（9/30）。中央寄せの flex の中では maxHeight: 100% が Safari で効かないことがあるので、
  // 高さの上限は画面の高さ（innerHeight）から出し、見出しと保存ボタンの行は固定して中身だけをスクロールさせる。inset は古い Safari が読まないので使わない。
  // 重なり順はヘッダー（HeaderBar）・通知（react-toastify）と同じ 9999。同じ値なら後に描いたものが上に出るので、
  // ヘッダーより後に描くこのモーダルはヘッダーの上に、アプリの最後に置いた通知はモーダルの上に出る。200 だとヘッダーが見出しを隠していた
  overlay: css({
    position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: `${OVERLAY_PAD}px`,
  }),
  panel: css({
    background: '#fff', borderRadius: '8px', width: '520px', maxWidth: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
  }),
  head: css({ padding: '14px 20px', borderBottom: `1px solid ${mock.gray10}`, flex: 'none' }),
  body: css({
    padding: '18px 20px', flex: '1 1 auto', minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
  }),
  h: css({ fontSize: '16px', fontWeight: 700, margin: '16px 0' }),
  wishRow: css({
    display: 'flex', alignItems: 'center', gap: '8px', background: mock.primaryM95, padding: '8px 12px',
  }),
  optBtn: css({
    border: `1px solid ${mock.gray20}`,
background: '#fff',
color: mock.textMain,
borderRadius: '5px',
padding: '10px 0',
cursor: 'pointer',
flex: 1,
fontFamily: 'inherit',
minWidth: '84px',
    '&:disabled': { cursor: 'default', opacity: 0.7 },
  }),
  sel: css({
    display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', background: mock.primaryM95, padding: '8px 12px',
  }),
  input: css({
    height: '32px', border: `1px solid ${mock.gray10}`, borderRadius: '4px', padding: '0 6px', fontSize: '13px', background: '#fff', color: mock.textMain, fontFamily: 'inherit',
  }),
  foot: css({
    padding: '12px 20px', borderTop: `1px solid ${mock.gray10}`, display: 'flex', alignItems: 'center', flex: 'none',
  }),
  ghostBtn: css({
    background: 'transparent', border: 'none', color: mock.textSub, fontSize: '13px', cursor: 'pointer', padding: '8px 4px', fontFamily: 'inherit',
  }),
  primaryBtn: css({
    background: mock.primary,
border: 'none',
borderRadius: '5px',
color: '#fff',
fontSize: '13px',
fontWeight: 700,
padding: '11px 24px',
cursor: 'pointer',
whiteSpace: 'nowrap',
fontFamily: 'inherit',
    '&:disabled': { background: mock.gray20, cursor: 'default' },
  }),
  dangerBtn: css({
    background: mock.secondary, border: 'none', borderRadius: '5px', color: '#fff', fontSize: '13px', fontWeight: 700, padding: '11px 20px', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
  }),
  outLink: css({
    marginLeft: '6px', color: mock.primary, fontWeight: 600, textDecoration: 'underline',
  }),
  appBtn: css({
    background: '#fff',
border: `1px solid ${mock.gray20}`,
borderRadius: '5px',
color: mock.textMain,
fontSize: '11px',
padding: '5px 10px',
cursor: 'pointer',
fontFamily: 'inherit',
    '&:disabled': { color: mock.gray40, cursor: 'default' },
  }),
};

export type ApplicationKind = 'stamp' | 'holiday' | 'overtime' | 'holidaywork' | 'transfer';
const APPLICATIONS: Array<{ kind: ApplicationKind; label: string }> = [
  { kind: 'stamp', label: '打刻修正申請' },
  { kind: 'holiday', label: '休暇申請' },
  { kind: 'overtime', label: '残業申請' },
  { kind: 'holidaywork', label: '休日出勤申請' },
  { kind: 'transfer', label: '振替申請' },
];

export type CellEditModalProps = {
  open: boolean;
  board: BoardData;
  staff: StaffInfo | null;
  day: DayHeader | null;
  /** 編集対象。null なら追加 */
  shift: SimulationShift | null;
  /** 同じセルの他のシフト（重複チェック用） */
  others: Array<SimulationShift>;
  hope: HopeLabel;
  free: FreeTimes;
  colorOf: (patternId: string | null) => PatternColor;
  editable: boolean;
  presetStart?: string | null;
  /** 時間量の整形。60 進/10 進とその丸めは労務規約項目マスタに合わせる（9/23） */
  formatTime?: TimeFormatter;
  onClose: () => void;
  onSave: (shift: SimulationShift) => void;
  onRemove: () => void;
  onApplication?: (kind: ApplicationKind) => void;
};

type Choice = 'pattern' | 'holiday' | 'free';

const autoBreak = (minutes: number): number => (minutes >= 480 ? 60 : minutes >= 360 ? 45 : minutes > 240 ? 15 : 0);

/**
 * モック準拠のセル編集（勤務パターン／時間／休憩／業務／勤務店舗／メモ）。1 件ずつ編集する
 */
const CellEditModal: React.FC<CellEditModalProps> = ({
  open, board, staff, day, shift, others, hope, free, colorOf, editable, presetStart, onClose, onSave, onRemove, onApplication,
  formatTime = defaultTimeFormatter,
}) => {
  const [choice, setChoice] = useState<Choice>('pattern');
  const [patternId, setPatternId] = useState<string | null>(null);
  const [isLegal, setIsLegal] = useState(false);
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('17:00');
  const [breakMin, setBreakMin] = useState<number | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [workOrg, setWorkOrg] = useState('');
  const [memo, setMemo] = useState('');
  const [error, setError] = useState<string | null>(null);
  // 画面の高さ（キーボードや回転で変わる）。パネルの高さの上限に使う
  const [viewportH, setViewportH] = useState(window.innerHeight);
  useEffect(() => {
    const onResize = () => setViewportH(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // 実績の確認は勤務スケジュールで行う（9/9 決定）。その画面の権限がある人にだけ出す
  const schedulePath = day ? dailyShiftPath(board.org.orgName, board.org.orgCode, day.date) : null;

  useEffect(() => {
    if (!open || !staff) {
      return;
    }
    setError(null);
    if (shift) {
      setChoice(shift.attendType !== 0 ? 'holiday' : shift.shiftPatternId ? 'pattern' : 'free');
      setPatternId(shift.shiftPatternId);
      setIsLegal(shift.attendType === 1);
      setStart(shift.start || free.start);
      setEnd(shift.end || free.end);
      setBreakMin(shift.breakMinutes);
      setBusinessId(shift.businessId || staff.mainBusinessId);
      setWorkOrg(shift.workOrgCode || board.org.orgCode);
      setMemo(shift.memo || '');
    } else {
      const first = board.shiftPatterns[0];
      setChoice(presetStart ? 'free' : first ? 'pattern' : 'free');
      setPatternId(first ? first.shiftPatternId : null);
      setIsLegal(false);
      setStart(presetStart || free.start);
      setEnd(presetStart ? '' : free.end);
      setBreakMin(free.breakMinutes);
      setBusinessId(staff.mainBusinessId);
      setWorkOrg(board.org.orgCode);
      setMemo('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || !staff || !day) {
    return null;
  }
  const d = parseDateStr(day.date);
  const pattern = patternId ? board.shiftPatterns.filter((p) => p.shiftPatternId === patternId)[0] : null;
  const patternNext = !!(pattern && pattern.details.length > 0 && pattern.details[pattern.details.length - 1].isEndNextDay);
  const span = choice === 'free' ? spanMinutes(start, end, false) : pattern && pattern.start && pattern.end ? spanMinutes(pattern.start, pattern.end, patternNext) : 0;
  const brk = breakMin === null ? autoBreak(span) : breakMin;
  const workOrgs = [{ orgCode: board.org.orgCode, orgName: board.org.orgName }, ...board.supportOrgs];
  const isSupport = workOrg !== board.org.orgCode && workOrg !== staff.belongOrgCode;
  const businesses = board.businesses.filter((b) => !b.isBreak);
  const disabled = !editable || day.locked;

  const build = (): SimulationShift | string => {
    const base = {
      tempId: shift ? shift.tempId : `n-${Date.now()}`,
      shiftId: shift ? shift.shiftId : undefined,
      expectedUpdateDate: shift ? shift.expectedUpdateDate : undefined,
    };
    if (choice === 'holiday') {
      if (staff.isGuest) {
        return '支援スタッフには公休を登録できません。';
      }
      return {
        ...base, attendType: isLegal ? 1 : 2, isLegal, shiftPatternId: null, start: null, end: null, isEndNextDay: false, breakMinutes: null, workMinutes: 0, businessId: null, workOrgCode: board.org.orgCode, memo, support: null,
      };
    }
    let s = start;
    let e = end;
    let nextDay = false;
    let pid: string | null = null;
    let shortName: string | null = null;
    if (choice === 'pattern') {
      if (!pattern || !pattern.start || !pattern.end) {
        return 'パターンを選んでください。';
      }
      s = pattern.start;
      e = pattern.end;
      nextDay = patternNext;
      pid = pattern.shiftPatternId;
      shortName = pattern.shortName;
    } else {
      const v = validateTimes(s, e);
      if (!v.ok) {
        return v.message;
      }
      if (spanMinutes(s, e, false) <= 0) {
        nextDay = true;
      }
    }
    const c = colorOf(pid);
    const cand: SimulationShift = {
      ...base,
attendType: 0,
isLegal: null,
shiftPatternId: pid,
shiftPatternShortName: shortName,
color: pid ? c.bg : null,
      start: s,
end: e,
isEndNextDay: nextDay,
breakMinutes: breakMin,
businessId,
workOrgCode: workOrg,
memo,
      support: isSupport ? { deadline: null, note: null, requestApproval: false } : null,
    };
    cand.workMinutes = workMinutesOf(s, e, nextDay, breakMin === null ? autoBreak(spanMinutes(s, e, nextDay)) : breakMin);
    return cand;
  };
  const save = () => {
    const r = build();
    if (typeof r === 'string') {
      setError(r);
      return;
    }
    const v = validateCell(others, r);
    if (!v.ok) {
      setError(v.message);
      return;
    }
    onSave(r);
  };

  const optStyle = (on: boolean, c?: PatternColor) => ({
    background: on ? (c ? c.bg : mock.primaryM90) : '#fff', color: on ? (c ? c.fg : mock.primary) : mock.textMain, borderColor: on ? (c ? 'transparent' : mock.primary) : mock.gray20, fontWeight: on ? 700 : 400,
  });
  const patternValue = choice === 'pattern' ? (patternId ? `p:${patternId}` : '') : choice === 'holiday' ? (isLegal || !staff.distinctionHoliday ? 'h:legal' : 'h:normal') : 'free';
  // 勤務パターンの選択肢。公休はスタッフごとに出し分ける（旧画面と同じ単位）。区別しない人の「公休」は法定で入れる（10/5）
  const patternOptions = [
    ...board.shiftPatterns.map((p) => ({
      value: `p:${p.shiftPatternId}`,
      label: `${p.name}${p.start && p.end ? `（${p.start}–${p.end}）` : '（時間未設定）'}`,
    })),
    ...(!staff.isGuest && staff.distinctionHoliday ? [{ value: 'h:legal', label: '公休（法定）' }] : []),
    ...(!staff.isGuest ? [{ value: staff.distinctionHoliday ? 'h:normal' : 'h:legal', label: staff.distinctionHoliday ? '公休（所定）' : '公休' }] : []),
    { value: 'free', label: 'フリー（時間を指定）' },
  ];
  const pickPattern = (v: string) => {
    if (v === 'free') {
      setChoice('free');
      return;
    }
    if (v === 'h:legal' || v === 'h:normal') {
      setChoice('holiday');
      setIsLegal(v === 'h:legal');
      return;
    }
    setChoice('pattern');
    setPatternId(v ? v.slice(2) : null);
  };
  const swatch = choice === 'holiday' ? { bg: mock.gray10, fg: mock.gray60 } : choice === 'free' ? { bg: mock.freeBg, fg: mock.freeText } : colorOf(patternId);
  const hopeName = hope && !hope.isOff ? hope.label : '';

  return (
    <div css={styles.overlay} onClick={onClose}>
      <div css={styles.panel} style={{ maxHeight: `${viewportH - OVERLAY_PAD * 2}px` }} onClick={(e) => e.stopPropagation()}>
        <div css={styles.head}>
          <div style={{ fontSize: '15px', fontWeight: 700 }}>{`${staff.staffName} / ${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日（${DOW_LABELS[d.getDay()]}）`}</div>
          <div style={{ fontSize: '11px', color: mock.textSub, marginTop: '2px' }}>{`${staff.employmentName || ''}${staff.mainBusinessName ? ` / ${staff.mainBusinessName}` : ''}${staff.isGuest ? `（支援 / ${staff.belongOrgName}所属）` : ''}`}</div>
        </div>
        <div css={styles.body}>
          <div css={styles.wishRow}>
            <span style={{ fontSize: '11px', color: mock.textSub }}>本人の希望</span>
            <span style={{ fontSize: '13px', fontWeight: 700 }}>{hope ? (hope.isOff ? '休み' : hopeName) : '未提出'}</span>
            {!disabled && hope && (hope.patternId || (hope.start && hope.end)) && (
              <button
                type="button"
                css={styles.appBtn}
                style={{
 marginLeft: 'auto', borderColor: mock.primary, color: mock.primary, fontWeight: 700,
}}
                onClick={() => {
                  // パターンの希望はそのパターン、時間の希望はその時間のフリーシフトにする
                  if (hope && hope.patternId) {
                    setChoice('pattern');
                    setPatternId(hope.patternId);
                  } else if (hope && hope.start && hope.end) {
                    setChoice('free');
                    setStart(hope.start.padStart(5, '0'));
                    setEnd(hope.end.padStart(5, '0'));
                  }
                }}
              >
                希望どおりにする
              </button>
            )}
          </div>

          <div css={styles.h}>勤務パターン</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              width: '18px', height: '18px', borderRadius: '4px', flex: 'none', background: swatch.bg, border: `1px solid ${mock.gray20}`,
            }}
            />
            {/* ほかの画面と同じ共通の Select（10/7）。未選択のときは「選択してください」 */}
            <BoardSelect
              id="cell-pattern"
              label="勤務パターン"
              value={patternValue}
              onChange={pickPattern}
              options={patternOptions}
              disabled={disabled}
              style={{ flex: 1 }}
            />
          </div>
          {choice === 'free' && (
            <div css={styles.sel}>
              <span style={{ fontSize: '12px', fontWeight: 500 }}>勤務時間</span>
              <input css={styles.input} type="time" step={300} value={start} onChange={(e) => setStart(e.target.value)} disabled={disabled} />
              <span style={{ fontSize: '12px' }}>–</span>
              <input css={styles.input} type="time" step={300} value={end} onChange={(e) => setEnd(e.target.value)} disabled={disabled} />
              <span style={{ fontSize: '11px', color: mock.textSub }}>{end && start && spanMinutes(start, end, false) <= 0 ? '（翌日まで）' : ''}</span>
            </div>
          )}
          {choice !== 'holiday' && (
            <div css={styles.sel}>
              <span style={{ fontSize: '12px', fontWeight: 500 }}>休憩</span>
              <input css={styles.input} style={{ width: '76px' }} type="number" min={0} max={240} step={5} value={brk} onChange={(e) => setBreakMin(Math.max(0, Math.min(240, Math.round(parseInt(e.target.value || '0', 10) / 5) * 5)))} disabled={disabled} />
              <span style={{ fontSize: '12px' }}>分（5分単位）</span>
              <button type="button" css={styles.appBtn} onClick={() => setBreakMin(null)} disabled={breakMin === null || disabled} title="雇用形態の自動休憩に任せる">自動</button>
              <span style={{ fontSize: '12px', color: mock.textSub, marginLeft: 'auto' }}>{`実働 ${formatTime(Math.max(0, span - brk))}`}</span>
            </div>
          )}

          {choice !== 'holiday' && businesses.length > 0 && (
            <React.Fragment>
              <div css={styles.h}>業務</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {businesses.map((b) => (
                  <button
                    key={b.businessId}
                    type="button"
                    css={styles.optBtn}
                    style={{
 ...optStyle(businessId === b.businessId), flex: 'none', padding: '9px 20px', fontSize: '13px',
}}
                    onClick={() => setBusinessId(b.businessId)}
                    disabled={disabled}
                  >
                    {b.businessName}
                  </button>
                ))}
              </div>
            </React.Fragment>
          )}

          {choice !== 'holiday' && !staff.isGuest && workOrgs.length > 1 && (
            <React.Fragment>
              <div css={styles.h}>勤務店舗</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BoardSelect
                  id="cell-work-org"
                  label="勤務店舗"
                  value={workOrg}
                  onChange={setWorkOrg}
                  options={workOrgs.map((o) => ({ value: o.orgCode, label: `${o.orgName}${o.orgCode === staff.belongOrgCode ? '（所属）' : ''}` }))}
                  disabled={disabled || !!shift}
                  style={{ flex: 1 }}
                />
                {!!shift && <span style={{ fontSize: '11px', color: mock.textSub, flex: 'none' }}>登録済みのシフトは変更できません</span>}
              </div>
              {isSupport && (
                <div style={{
 marginTop: '8px', background: mock.guestBg, border: `1px solid ${mock.guestText}`, padding: '8px 12px', fontSize: '11px', color: mock.guestText,
}}
                >
                  {`支援シフトとして登録されます。人数・人件費は ${(workOrgs.filter((o) => o.orgCode === workOrg)[0] || { orgName: workOrg }).orgName} に計上され、${board.org.orgName} では「他店へ支援」として表示されます。`}
                </div>
              )}
            </React.Fragment>
          )}

          <div css={styles.h}>勤務メモ</div>
          <input
            css={styles.input}
            style={{
 width: '100%', height: '36px', padding: '0 10px', boxSizing: 'border-box',
}}
            value={memo}
            maxLength={100}
            placeholder="例：ミーティングあり / レジ締め担当"
            onChange={(e) => setMemo(e.target.value)}
            disabled={disabled}
          />

          {onApplication && !staff.isGuest && (
            <div style={{ marginTop: '16px', paddingTop: '10px', borderTop: `1px solid ${mock.gray10}` }}>
              <div style={{ fontSize: '11px', color: mock.textSub, marginBottom: '6px' }}>申請（既存の申請画面を開きます）</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {APPLICATIONS.map((a) => <button key={a.kind} type="button" css={styles.appBtn} onClick={() => onApplication(a.kind)}>{a.label}</button>)}
              </div>
              {schedulePath && (
                <div style={{ marginTop: '8px', fontSize: '11px', color: mock.textSub }}>
                  打刻の実績はこの画面では扱いません。
                  <Link to={schedulePath} css={styles.outLink} onClick={onClose}>勤務スケジュールで確認する</Link>
                </div>
              )}
            </div>
          )}
          {error && <div style={{ marginTop: '10px', color: mock.secondary, fontSize: '12px' }}>{error}</div>}
          {day.locked && <div style={{ marginTop: '10px', color: mock.amberText, fontSize: '12px' }}>{day.lockReason === 'SHIFT_CONFIRMED' ? 'この日は公開済みのためロックされています。「ロック解除」を押すと編集できます。' : 'この日はロックされています（本部確定済みなど）。'}</div>}
        </div>
        <div css={styles.foot}>
          <button type="button" css={styles.ghostBtn} onClick={onClose}>キャンセル</button>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button type="button" css={styles.primaryBtn} onClick={save} disabled={disabled}>保存</button>
            {shift && !disabled && <button type="button" css={styles.dangerBtn} onClick={onRemove}>シフトを取り消す</button>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CellEditModal;
